import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, mutateJsonWithAuth } from '@/lib/api'
import type {
  CobaltConnectTokenRequest,
  CobaltConnectTokenResponse,
  CobaltSyncAvailabilityWindows,
  CobaltSyncConfig,
  CobaltSyncConfigOverviewResponse,
  CobaltSyncConfigUpdate,
  CobaltSyncEntity,
  CobaltSyncOverviewResponse,
  CobaltSyncRun,
  CobaltTokenExchangeResponse,
  CredentialIntakeLinkResponse,
  CredentialPromotionResponse,
  CredentialStatusResponse,
  CredentialValidationResponse,
  ReportDownloadStatusResponse,
  ReportType,
} from './types'

export function createCobaltConnectToken(
  token: string,
  request: CobaltConnectTokenRequest
): Promise<CobaltConnectTokenResponse> {
  return mutateJsonWithAuth<CobaltConnectTokenResponse>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/connect-token`,
    method: 'POST',
    body: request,
    errorMessage: 'Failed to start connection setup',
  })
}

export function exchangeCobaltPublicToken(
  token: string,
  publicToken: string
): Promise<CobaltTokenExchangeResponse> {
  return mutateJsonWithAuth<CobaltTokenExchangeResponse>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/token-exchange`,
    method: 'POST',
    body: { public_token: publicToken },
    errorMessage: 'Failed to complete connection setup',
  })
}

export function fetchCobaltSyncs(
  token: string,
  {
    referralLookbackDays,
    orderLookbackDays,
  }: CobaltSyncAvailabilityWindows = {},
  signal?: AbortSignal
): Promise<CobaltSyncOverviewResponse> {
  const params = new URLSearchParams()
  if (referralLookbackDays !== undefined)
    params.set('referral_lookback_days', String(referralLookbackDays))
  if (orderLookbackDays !== undefined)
    params.set('order_lookback_days', String(orderLookbackDays))
  const query = params.size ? `?${params}` : ''
  return fetchJsonWithAuth<CobaltSyncOverviewResponse>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/syncs${query}`,
    signal,
    errorMessage: 'Failed to load sync status',
  })
}

export function fetchCobaltSyncConfigs(
  token: string,
  signal?: AbortSignal
): Promise<CobaltSyncConfigOverviewResponse> {
  return fetchJsonWithAuth<CobaltSyncConfigOverviewResponse>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/syncs/configs`,
    signal,
    errorMessage: 'Failed to load sync configuration',
  })
}

export function updateCobaltSyncConfig(
  token: string,
  entity: CobaltSyncEntity,
  changes: CobaltSyncConfigUpdate
): Promise<CobaltSyncConfig> {
  return mutateJsonWithAuth<CobaltSyncConfig>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/syncs/configs/${entity}`,
    method: 'PATCH',
    body: changes,
    errorMessage: `Failed to update ${entity} sync configuration`,
  })
}

export function startCobaltSync(
  token: string,
  entity: CobaltSyncEntity
): Promise<CobaltSyncRun> {
  return mutateJsonWithAuth<CobaltSyncRun>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/syncs/${entity}`,
    method: 'POST',
    errorMessage: `Failed to start ${entity} sync`,
  })
}

export function cancelCobaltSync(
  token: string,
  entity: CobaltSyncEntity
): Promise<CobaltSyncRun> {
  return mutateJsonWithAuth<CobaltSyncRun>({
    token,
    url: `${AGENT_URL}/connections/ecw-cobalt/syncs/${entity}`,
    method: 'DELETE',
    errorMessage: `Failed to cancel ${entity} sync`,
  })
}

export function fetchEcwCredentialStatus(
  token: string,
  signal?: AbortSignal
): Promise<CredentialStatusResponse> {
  return fetchJsonWithAuth<CredentialStatusResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/status`,
    signal,
    errorMessage: 'Failed to load credential status',
  })
}

export function createEcwCredentialIntakeLink(
  token: string
): Promise<CredentialIntakeLinkResponse> {
  return mutateJsonWithAuth<CredentialIntakeLinkResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/intake-link`,
    method: 'POST',
    errorMessage: 'Failed to create credential intake link',
  })
}

export function fetchEcwCredentialValidation(
  token: string,
  candidateVersion: number,
  signal?: AbortSignal
): Promise<CredentialValidationResponse> {
  return fetchJsonWithAuth<CredentialValidationResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/validation?candidate_version=${candidateVersion}`,
    signal,
    errorMessage: 'Failed to load credential validation',
  })
}

export function createEcwCredentialValidation(
  token: string
): Promise<CredentialValidationResponse> {
  return mutateJsonWithAuth<CredentialValidationResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/validation`,
    method: 'POST',
    errorMessage: 'Failed to start credential validation',
  })
}

export function promoteEcwCredential(
  token: string
): Promise<CredentialPromotionResponse> {
  return mutateJsonWithAuth<CredentialPromotionResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/promotion`,
    method: 'POST',
    errorMessage: 'Failed to approve credential connection',
  })
}

export function fetchReportDownloadStatus(
  token: string,
  reportType: ReportType,
  signal?: AbortSignal
): Promise<ReportDownloadStatusResponse> {
  return fetchJsonWithAuth<ReportDownloadStatusResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/reports/${reportType}/status`,
    signal,
    errorMessage: 'Failed to load report download status',
  })
}

export function runReportDownload(
  token: string,
  reportType: ReportType
): Promise<ReportDownloadStatusResponse> {
  return mutateJsonWithAuth<ReportDownloadStatusResponse>({
    token,
    url: `${AGENT_URL}/credentials/ecw/reports/${reportType}/run`,
    method: 'POST',
    errorMessage: 'Failed to start report download',
  })
}
